import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setTicketUnreadCount, selectLastInboxEvent } from "../../store/auth/authSlice";
import { Tag, Empty, Tooltip, Button } from "antd";
import {
  ArrowLeftOutlined, SyncOutlined, CheckCircleOutlined, CloseCircleOutlined,
  InboxOutlined, UserOutlined, CalendarOutlined,
  MailOutlined, SendOutlined, ClockCircleOutlined,
  BoldOutlined, ItalicOutlined, UnderlineOutlined, OrderedListOutlined,
  UnorderedListOutlined, AlignLeftOutlined, AlignCenterOutlined, AlignRightOutlined,
  LinkOutlined, UndoOutlined, RedoOutlined, CodeOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import { fetchTicketDetail } from "../services/ticketService";
import { EMAIL_API_URL } from "../../config";

function RichTextEditor({ value, onChange, placeholder }) {
  const editorRef = useRef(null);
  const [showHtml, setShowHtml] = useState(false);
  const [htmlValue, setHtmlValue] = useState("");
  const isInternal = useRef(false);
  const pendingHtml = useRef(null);

  useEffect(() => {
    if (!editorRef.current) return;
    if (editorRef.current.innerHTML !== (value || "") && !isInternal.current)
      editorRef.current.innerHTML = value || "";
    isInternal.current = false;
  }, [value]);

  useEffect(() => {
    if (!showHtml && pendingHtml.current !== null && editorRef.current) {
      editorRef.current.innerHTML = pendingHtml.current;
      pendingHtml.current = null;
      emit();
    }
  }, [showHtml]); // eslint-disable-line

  const emit = useCallback(() => {
    if (!editorRef.current) return;
    isInternal.current = true;
    onChange?.(editorRef.current.innerHTML);
  }, [onChange]);

  const exec = (cmd, val = null) => { editorRef.current?.focus(); document.execCommand(cmd, false, val); emit(); };

  const btn = (icon, cmd, val, title) => (
    <Tooltip title={title} key={cmd + (val || "")}>
      <button type="button" onMouseDown={(e) => { e.preventDefault(); exec(cmd, val); }}
        style={{ background: "none", border: "none", cursor: "pointer", padding: "4px 7px", borderRadius: 4, fontSize: 13, color: "#374151", display: "inline-flex", alignItems: "center" }}
        onMouseEnter={(e) => e.currentTarget.style.background = "#f3f4f6"}
        onMouseLeave={(e) => e.currentTarget.style.background = "none"}
      >{icon}</button>
    </Tooltip>
  );

  return (
    <div style={{ border: "1px solid #d9d9d9", borderRadius: 10, overflow: "hidden", background: "#fff" }}>
      <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2, padding: "6px 10px", borderBottom: "1px solid #f0f0f0", background: "#fafafa" }}>
        {btn(<BoldOutlined />, "bold", null, "Bold")}
        {btn(<ItalicOutlined />, "italic", null, "Italic")}
        {btn(<UnderlineOutlined />, "underline", null, "Underline")}
        <div style={{ width: 1, height: 18, background: "#e5e7eb", margin: "0 4px" }} />
        {btn(<UnorderedListOutlined />, "insertUnorderedList", null, "Bullet list")}
        {btn(<OrderedListOutlined />, "insertOrderedList", null, "Numbered list")}
        <div style={{ width: 1, height: 18, background: "#e5e7eb", margin: "0 4px" }} />
        {btn(<AlignLeftOutlined />, "justifyLeft", null, "Align left")}
        {btn(<AlignCenterOutlined />, "justifyCenter", null, "Align center")}
        {btn(<AlignRightOutlined />, "justifyRight", null, "Align right")}
        <div style={{ width: 1, height: 18, background: "#e5e7eb", margin: "0 4px" }} />
        <Tooltip title="Insert link">
          <button type="button"
            onMouseDown={(e) => { e.preventDefault(); const url = window.prompt("Enter URL"); if (url) exec("createLink", url); }}
            style={{ background: "none", border: "none", cursor: "pointer", padding: "4px 7px", borderRadius: 4, fontSize: 13, color: "#374151", display: "inline-flex", alignItems: "center" }}
            onMouseEnter={(e) => e.currentTarget.style.background = "#f3f4f6"}
            onMouseLeave={(e) => e.currentTarget.style.background = "none"}
          ><LinkOutlined /></button>
        </Tooltip>
        {btn(<UndoOutlined />, "undo", null, "Undo")}
        {btn(<RedoOutlined />, "redo", null, "Redo")}
        <div style={{ flex: 1 }} />
        <Button size="small" icon={<CodeOutlined />} onClick={() => {
          if (!showHtml) setHtmlValue(editorRef.current?.innerHTML || "");
          else pendingHtml.current = htmlValue;
          setShowHtml(v => !v);
        }} style={{ fontSize: 11, borderRadius: 6, background: showHtml ? "#1a1a2e" : "transparent", color: showHtml ? "#fff" : "#374151", border: "1px solid #d9d9d9" }}>
          {showHtml ? "Hide HTML" : "View HTML"}
        </Button>
      </div>
      {showHtml
        ? <textarea value={htmlValue} onChange={(e) => setHtmlValue(e.target.value)} rows={12}
            style={{ width: "100%", boxSizing: "border-box", padding: "12px 14px", border: "none", outline: "none", fontFamily: "monospace", fontSize: 12, lineHeight: 1.6, color: "#1a1a2e", resize: "vertical", background: "#f8fafc" }} />
        : <div ref={editorRef} contentEditable suppressContentEditableWarning onInput={emit}
            data-placeholder={placeholder}
            style={{ minHeight: 130, padding: "12px 14px", outline: "none", fontSize: 13, lineHeight: 1.8, color: "#1a1a2e", overflowY: "auto" }} />
      }
    </div>
  );
}

const STATUS_CFG = {
  open:        { label: "Open",        color: "#2563eb", bg: "#eff6ff", icon: <InboxOutlined />,       gradient: "linear-gradient(135deg,#2563eb,#1d4ed8)" },
  in_progress: { label: "In Progress", color: "#d97706", bg: "#fffbeb", icon: <SyncOutlined spin />,   gradient: "linear-gradient(135deg,#d97706,#b45309)" },
  resolved:    { label: "Resolved",    color: "#16a34a", bg: "#f0fdf4", icon: <CheckCircleOutlined />, gradient: "linear-gradient(135deg,#16a34a,#15803d)" },
  closed:      { label: "Closed",      color: "#64748b", bg: "#f1f5f9", icon: <CloseCircleOutlined />, gradient: "linear-gradient(135deg,#64748b,#475569)" },
};

const PRIORITY_CFG = {
  high:   { label: "High",   color: "#ea580c", bg: "#fff7ed", dot: "#ea580c" },
  medium: { label: "Medium", color: "#ca8a04", bg: "#fefce8", dot: "#ca8a04" },
  low:    { label: "Low",    color: "#64748b", bg: "#f8fafc", dot: "#94a3b8" },
};

const TYPE_CFG = {
  bug:              { label: "Bug",              color: "#dc2626" },
  feature_request:  { label: "Feature Request",  color: "#16a34a" },
  billing:          { label: "Billing",          color: "#7c3aed" },
  general_feedback: { label: "General Feedback", color: "#64748b" },
  other:            { label: "Other",            color: "#0891b2" },
};

const statusBadge = (s) => {
  const c = STATUS_CFG[s] || { label: s, color: "#64748b", bg: "#f1f5f9", icon: <ClockCircleOutlined /> };
  return <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 14px", borderRadius: 20, fontSize: 12, fontWeight: 700, color: c.color, background: c.bg, border: `1px solid ${c.color}30`, whiteSpace: "nowrap" }}>{c.icon} {c.label}</span>;
};

const priorityBadge = (p) => {
  const c = PRIORITY_CFG[p] || { label: p, color: "#64748b", bg: "#f8fafc", dot: "#94a3b8" };
  return <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 12px", borderRadius: 20, fontSize: 11, fontWeight: 700, color: c.color, background: c.bg, textTransform: "uppercase", letterSpacing: 0.6, border: `1px solid ${c.color}25` }}><span style={{ width: 6, height: 6, borderRadius: "50%", background: c.dot }} />{c.label}</span>;
};

const typeTag = (t) => {
  const c = TYPE_CFG[t] || { label: t || "—", color: "#64748b" };
  return <Tag style={{ fontSize: 12, fontWeight: 600, color: c.color, borderColor: `${c.color}35`, background: `${c.color}0d`, borderRadius: 6 }}>{c.label}</Tag>;
};

const parseAttachments = (a) => {
  if (Array.isArray(a)) return a;
  if (typeof a === "string" && a.trim().startsWith("[")) {
    try { return JSON.parse(a); } catch { return []; }
  }
  return [];
};

const timeAgo = (d) => {
  if (!d) return "—";
  const m = Math.floor((Date.now() - new Date(d)) / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const dy = Math.floor(h / 24);
  if (dy < 30) return `${dy}d ago`;
  return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const fmtDate = (d) => d ? new Date(d).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

const initials = (n) => (n || "U").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

const Shimmer = ({ h = 14, w = "100%", r = 8 }) => (
  <div style={{ height: h, width: w, borderRadius: r, background: "linear-gradient(90deg,#e2e8f0 25%,#f1f5f9 50%,#e2e8f0 75%)", backgroundSize: "200% 100%", animation: "shimmer 1.4s infinite" }} />
);

// ─────────────────────────────────────────────────────────────────────────────
const TicketDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const [ticket, setTicket]             = useState(null);
  const [loading, setLoading]           = useState(true);

  const [emailThread, setEmailThread]   = useState([]);
  const [emailLoading, setEmailLoading]       = useState(false);
  const [showCompose, setShowCompose]         = useState(false);
  const [composeSubject, setSubject]    = useState("");
  const [composeBody, setBody]          = useState("");
  const [attachments, setAttachments]   = useState([]);
  const [sendingEmail, setSending]      = useState(false);
  const [conversationId, setConversationId] = useState(location.state?.conversation_id || null);
  const fileInputRef = useRef(null);


  const internalUserId = Number(localStorage.getItem("trade_user_id"));
  const senderEmail = (() => { try { return JSON.parse(localStorage.getItem("trade_user_data"))?.email || ""; } catch { return ""; } })();
  const authHdr = () => ({ Authorization: `Bearer ${localStorage.getItem("cms_token")}` });

  // ── 1. Load ticket
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await fetchTicketDetail(id);
        const t = res?.data || null;
        if (t) {
          let atts = t.attachments;
          if (typeof atts === "string") { try { atts = JSON.parse(atts); } catch { atts = []; } }
          t.attachments = Array.isArray(atts) ? atts : (atts ? [atts] : []);
        }
        setTicket(t);
        if (t) {
          setSubject(`Re: [Ticket #${t.id}] ${t.subject || ""}`);
          if (t.conversation_id) setConversationId(t.conversation_id);
        }
      } catch {
        toast.error("Failed to load ticket");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  // ── 2. Load thread as soon as ticket is ready
  useEffect(() => {
    if (!ticket) return;
    const convId = ticket.conversation_id || conversationId;
    if (!convId) {
      setShowCompose(true); // no conversation yet — open compose
      return;
    }
    fetchThread(ticket.user_id, convId).then(thread => {
      if (!thread?.length) setShowCompose(true); // empty thread — open compose
    });
  }, [ticket?.id]); // eslint-disable-line

  // ── 5. Redux SSE fan-out: refetch thread when a matching inbound ticket event arrives
  const lastInboxEvent = useSelector(selectLastInboxEvent);
  useEffect(() => {
    if (!lastInboxEvent || lastInboxEvent.type !== "inbound" || lastInboxEvent.inbox !== "ticket") return;
    if (Number(lastInboxEvent.customer_id) !== Number(ticket?.user_id)) return;
    if (conversationId && lastInboxEvent.conversation_id !== conversationId) return;
    fetchThread(ticket.user_id, conversationId, true);
  }, [lastInboxEvent]); // eslint-disable-line

  const fetchThread = async (customerId, convId, silent = false) => {
    const resolvedConvId = convId ?? conversationId;
    if (!customerId || !resolvedConvId) return;
    if (!silent) setEmailLoading(true);
    try {
      const params = { internal_user_id: internalUserId };
      params.conversation_id = convId ?? conversationId;
      const res = await axios.get(`${EMAIL_API_URL}/api/single-email/thread/${customerId}`, {
        params,
        headers: authHdr(),
      });
      const raw = res.data?.data ?? res.data;
      const arr = Array.isArray(raw) ? raw : [];
      // Only show: outbound emails sent by current user + inbound emails that are replies to them
      const myOutbound = arr.filter(m => m.direction === "outbound" && m.from_email === senderEmail);
      const filtered = myOutbound.length > 0
        ? arr.filter(m => m.direction === "inbound" || (m.direction === "outbound" && m.from_email === senderEmail))
        : [];
      setEmailThread(filtered);
      // Mark emails as read and clear sidebar badge immediately
      axios.patch(`${EMAIL_API_URL}/api/single-email/mark-read/${customerId}`, {}, { headers: authHdr() })
        .then(() => dispatch(setTicketUnreadCount(0)))
        .catch(() => {});
      return filtered;
    } catch {
      setEmailThread([]);
      return [];
    } finally {
      setEmailLoading(false);
    }
  };

  const handleComposeClick = () => { setShowCompose(v => !v); if (showCompose) setAttachments([]); };

  const bodyText = () => composeBody.replace(/<[^>]*>/g, "").trim();

  const sendEmail = async () => {
    if (!bodyText() || !ticket?.user_email) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("from", senderEmail);
      fd.append("to", ticket.user_email);
      fd.append("subject", composeSubject);
      fd.append("body", composeBody);
      if (ticket.user_id) fd.append("customer_id", ticket.user_id);
      if (internalUserId) fd.append("internal_user_id", internalUserId);
      if (conversationId) fd.append("conversation_id", conversationId);
      attachments.forEach((file) => fd.append("attachments", file));
      const sendRes = await axios.post(`${EMAIL_API_URL}/api/single-email/send`, fd, { headers: authHdr() });
      // Store conversation_id from response (new thread) or keep existing
      const newConvId = sendRes.data?.conversation_id || sendRes.data?.data?.conversation_id;
      if (newConvId && !conversationId) {
        setConversationId(newConvId);
        axios.patch(`${EMAIL_API_URL}/api/tickets/${id}/conversation`, { conversation_id: newConvId }, { headers: authHdr() }).catch(() => {});
      }
      toast.success("Email sent");
      setShowCompose(false);
      setBody("");
      setAttachments([]);
      fetchThread(ticket.user_id, newConvId || conversationId);
    } catch {
      toast.error("Failed to send email");
    } finally {
      setSending(false);
    }
  };

  const tc = ticket ? (STATUS_CFG[ticket.status] || STATUS_CFG.open) : STATUS_CFG.open;
  const isOverdue = ticket && !["resolved", "closed"].includes(ticket.status) && (Date.now() - new Date(ticket.created_at) > ({ high: 8, medium: 24, low: 72 }[ticket.priority] || 24) * 3600000);

  return (
    <div className="main-content">
      <style>{`
        @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
        @keyframes fadeUp { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
        [contenteditable][data-placeholder]:empty:before { content:attr(data-placeholder);color:#9ca3af;pointer-events:none; }
        .td-card { background:#fff;border-radius:16px;border:1px solid #f0f4ff;box-shadow:0 1px 8px rgba(99,102,241,0.06); }
        .td-msg-scroll::-webkit-scrollbar { width:4px; }
        .td-msg-scroll::-webkit-scrollbar-track { background:transparent; }
        .td-msg-scroll::-webkit-scrollbar-thumb { background:#cbd5e1;border-radius:10px; }
      `}</style>

      <div className="page-content">
        <div className="container-fluid">

          {/* Breadcrumb */}
          <div className="row mb-3">
            <div className="col-12">
              <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <button onClick={() => navigate("/cms/tickets")} style={{ width: 36, height: 36, borderRadius: 10, border: "1.5px solid #e0e7ff", background: "#f5f3ff", color: "#6366f1", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: 15 }}>
                    <ArrowLeftOutlined />
                  </button>
                  <div>
                    <h4 style={{ margin: 0, fontWeight: 800, fontSize: 20, color: "#1e293b" }}>
                      {loading ? "Loading…" : `Ticket #${ticket?.id}`}
                    </h4>
                    <p style={{ margin: 0, fontSize: 12, color: "#94a3b8" }}>Email communication</p>
                  </div>
                </div>
                <ol className="breadcrumb m-0">
                  <li className="breadcrumb-item"><Link to="/cms/home">CMS</Link></li>
                  <li className="breadcrumb-item"><Link to="/cms/tickets">Tickets</Link></li>
                  <li className="breadcrumb-item active">{loading ? "…" : `#${ticket?.id}`}</li>
                </ol>
              </div>
            </div>
          </div>

          {loading ? (
            <div style={{ display: "flex", gap: 20 }}>
              <Shimmer h={600} w={280} r={12} />
              <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 12 }}>
                <Shimmer h={60} r={8} />
                <Shimmer h={500} r={12} />
              </div>
            </div>
          ) : !ticket ? (
            <div style={{ textAlign: "center", padding: 80 }}>
              <Empty description="Ticket not found" />
            </div>
          ) : (
            <div style={{ animation: "fadeUp 0.3s ease", display: "flex", gap: 20, alignItems: "flex-start" }}>

              {/* ── LEFT SIDEBAR ── */}
              <div style={{ width: 280, flexShrink: 0, display: "flex", flexDirection: "column", gap: 0, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, overflow: "hidden" }}>

                {/* Ticket ID + Subject */}
                <div style={{ background: tc.gradient, padding: "20px 18px" }}>
                  <div style={{ fontSize: 10, fontFamily: "monospace", letterSpacing: 1.5, fontWeight: 700, color: "rgba(255,255,255,0.7)", marginBottom: 6 }}>TICKET #{ticket.id}{isOverdue && <span style={{ marginLeft: 8, background: "#fef2f2", color: "#dc2626", padding: "1px 6px", borderRadius: 4, fontSize: 9 }}>OVERDUE</span>}</div>
                  <div style={{ fontWeight: 800, fontSize: 14, color: "#fff", lineHeight: 1.4, marginBottom: 12 }}>{ticket.subject || "—"}</div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {ticket.type && <Tag style={{ fontSize: 10, fontWeight: 700, color: "#fff", borderColor: "rgba(255,255,255,0.35)", background: "rgba(255,255,255,0.18)", borderRadius: 20, margin: 0 }}>{TYPE_CFG[ticket.type]?.label || ticket.type}</Tag>}
                    {ticket.priority && <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 10px", borderRadius: 20, fontSize: 10, fontWeight: 700, color: "#fff", background: "rgba(255,255,255,0.18)", border: "1px solid rgba(255,255,255,0.3)" }}><span style={{ width: 5, height: 5, borderRadius: "50%", background: PRIORITY_CFG[ticket.priority]?.dot || "#fff" }} />{PRIORITY_CFG[ticket.priority]?.label}</span>}
                  </div>
                </div>

                {/* Reporter */}
                <div style={{ padding: "14px 18px", borderBottom: "1px solid #f3f4f6" }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>Reported by</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 36, height: 36, borderRadius: "50%", background: tc.gradient, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 800, fontSize: 13, flexShrink: 0 }}>{initials(ticket.user_name)}</div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 13, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{ticket.user_name || "—"}</div>
                      <div style={{ fontSize: 11, color: "#6b7280", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{ticket.user_email || ""}</div>
                    </div>
                  </div>
                </div>

                {/* Info rows */}
                <div style={{ padding: "14px 18px", borderBottom: "1px solid #f3f4f6" }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>Ticket Info</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
                    {[
                      ["Status", statusBadge(ticket.status)],
                      ["Type", ticket.type ? <Tag style={{ fontSize: 10, fontWeight: 600, color: TYPE_CFG[ticket.type]?.color || "#64748b", borderColor: `${TYPE_CFG[ticket.type]?.color || "#64748b"}35`, background: `${TYPE_CFG[ticket.type]?.color || "#64748b"}0d`, borderRadius: 4, margin: 0 }}>{TYPE_CFG[ticket.type]?.label || ticket.type}</Tag> : "—"],
                      ["Priority", ticket.priority ? priorityBadge(ticket.priority) : "—"],
                      ["Reported", ticket.created_at ? new Date(ticket.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"],
                      ["Resolved", ticket.resolved_at ? new Date(ticket.resolved_at).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"],
                      ...(ticket.screen_size ? [["Screen", ticket.screen_size]] : []),
                    ].map(([label, val]) => (
                      <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 11, color: "#9ca3af", fontWeight: 600, flexShrink: 0 }}>{label}</span>
                        <span style={{ fontSize: 11, color: "#374151", textAlign: "right" }}>{val}</span>
                      </div>
                    ))}
                    {ticket.route_url && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                        <span style={{ fontSize: 11, color: "#9ca3af", fontWeight: 600 }}>Page URL</span>
                        <a href={ticket.route_url} target="_blank" rel="noreferrer" style={{ fontSize: 10, color: "#6366f1", wordBreak: "break-all" }}>{ticket.route_url}</a>
                      </div>
                    )}
                  </div>
                </div>

                {/* Description */}
                {ticket.description && (
                  <div style={{ padding: "14px 18px", borderBottom: "1px solid #f3f4f6" }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Description</div>
                    <div style={{ fontSize: 12, color: "#374151", lineHeight: 1.75, whiteSpace: "pre-wrap" }}>{ticket.description}</div>
                  </div>
                )}

                {/* Remarks */}
                {ticket.remarks && (
                  <div style={{ padding: "14px 18px", borderBottom: "1px solid #f3f4f6", background: "#f5f3ff" }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: "#4f46e5", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>✎ Your Remarks</div>
                    <div style={{ fontSize: 12, color: "#3730a3", lineHeight: 1.75, whiteSpace: "pre-wrap" }}>{ticket.remarks}</div>
                  </div>
                )}

                {/* Attachments */}
                {ticket.attachments?.length > 0 && (
                  <div style={{ padding: "14px 18px" }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>📎 Attachments ({ticket.attachments.length})</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {ticket.attachments.map((att, idx) => {
                        const isImage = att.mime?.startsWith("image/");
                        const sizeKB = att.size ? (att.size / 1024).toFixed(1) + " KB" : "";
                        return (
                          <div key={idx}>
                            {isImage && <a href={att.url} target="_blank" rel="noreferrer"><img src={att.url} alt={att.filename} style={{ width: "100%", maxHeight: 120, objectFit: "cover", borderRadius: 6, marginBottom: 4, display: "block" }} /></a>}
                            <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#f9fafb", borderRadius: 6, padding: "7px 10px", border: "1px solid #e5e7eb" }}>
                              <span style={{ fontSize: 14 }}>{isImage ? "🖼️" : "📄"}</span>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 11, fontWeight: 600, color: "#374151", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{att.filename}</div>
                                {sizeKB && <div style={{ fontSize: 10, color: "#9ca3af" }}>{sizeKB}</div>}
                              </div>
                              <a href={att.url} target="_blank" rel="noreferrer" download={att.filename} style={{ width: 26, height: 26, borderRadius: 6, background: "#eef2ff", color: "#6366f1", display: "inline-flex", alignItems: "center", justifyContent: "center", textDecoration: "none", flexShrink: 0, fontSize: 12 }}>⬇</a>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* ── RIGHT: EMAIL THREAD (full height) ── */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="td-card" style={{ padding: 0, overflow: "hidden" }}>

                  {/* Header */}
                  <div style={{ padding: "14px 22px", borderBottom: "1px solid #e8ecf0", display: "flex", alignItems: "center", justifyContent: "space-between", background: "#fff" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <MailOutlined style={{ color: "#6366f1", fontSize: 17 }} />
                      <span style={{ fontWeight: 700, fontSize: 15, color: "#1e293b" }}>Email Thread</span>
                      <span style={{ fontSize: 12, color: "#94a3b8" }}>({emailThread.length} message{emailThread.length !== 1 ? "s" : ""})</span>
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      {emailThread.length > 0 && (
                        <button onClick={handleComposeClick}
                          style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "6px 16px", borderRadius: 6, border: "none", background: showCompose ? "#e0e7ff" : "linear-gradient(135deg,#6366f1,#4f46e5)", color: showCompose ? "#6366f1" : "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                          <SendOutlined />
                          {showCompose ? "Cancel" : "Compose"}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Compose form */}
                  {showCompose && (
                    <div style={{ padding: "24px 28px", borderBottom: emailThread.length > 0 ? "1px solid #e8ecf0" : "none", background: "#fff" }}>
                      <div style={{ fontWeight: 700, fontSize: 15, color: "#1e293b", marginBottom: 20 }}>
                        {emailThread.length === 0 ? "Create new Support Request" : "Reply to Customer"}
                      </div>

                      {/* Name + Email row */}
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6 }}>Name</div>
                          <div style={{ padding: "9px 12px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13, color: "#6b7280", background: "#f9fafb" }}>{ticket.user_name || "—"}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6 }}>Email Address</div>
                          <div style={{ padding: "9px 12px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13, color: "#6b7280", background: "#f9fafb" }}>{ticket.user_email || "—"}</div>
                        </div>
                      </div>

                      {/* Subject */}
                      <div style={{ marginBottom: 16 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6 }}>Subject</div>
                        <input value={composeSubject} onChange={(e) => setSubject(e.target.value)}
                          style={{ width: "100%", padding: "9px 12px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13, outline: "none", color: "#374151", fontFamily: "inherit", boxSizing: "border-box" }}
                          onFocus={(e) => { e.target.style.borderColor = "#6366f1"; }}
                          onBlur={(e) => { e.target.style.borderColor = "#d1d5db"; }}
                        />
                      </div>

                      {/* Message */}
                      <div style={{ marginBottom: 16 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6 }}>Message <span style={{ color: "#dc2626" }}>*</span></div>
                        <RichTextEditor value={composeBody} onChange={setBody} placeholder="Write your email content here..." />
                      </div>

                      {/* Attachments */}
                      <div style={{ marginBottom: 20 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6 }}>Attachments</div>
                        <input ref={fileInputRef} type="file" multiple style={{ display: "none" }}
                          onChange={(e) => setAttachments((prev) => [...prev, ...Array.from(e.target.files)])} />
                        <div style={{ display: "flex", alignItems: "center", border: "1px solid #d1d5db", borderRadius: 6, overflow: "hidden" }}>
                          <span style={{ flex: 1, padding: "8px 12px", fontSize: 13, color: attachments.length > 0 ? "#374151" : "#9ca3af" }}>
                            {attachments.length > 0 ? `${attachments.length} file(s) selected` : "Choose file"}
                          </span>
                          <button type="button" onClick={() => fileInputRef.current?.click()}
                            style={{ padding: "8px 14px", background: "#374151", color: "#fff", border: "none", fontSize: 12, fontWeight: 600, cursor: "pointer", borderLeft: "1px solid #d1d5db" }}>Browse</button>
                          <button type="button" onClick={() => fileInputRef.current?.click()}
                            style={{ padding: "8px 14px", background: "#fff", color: "#374151", border: "none", fontSize: 12, fontWeight: 600, cursor: "pointer", borderLeft: "1px solid #d1d5db" }}>+ Add More</button>
                        </div>
                        <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>Allowed File Extensions: .jpg, .gif, .jpeg, .png (Max file size: 8MB)</div>
                        {attachments.length > 0 && (
                          <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 6 }}>
                            {attachments.map((f, i) => (
                              <div key={i} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 4, background: "#eef2ff", border: "1px solid #c7d2fe", fontSize: 11, color: "#4f46e5" }}>
                                📎 {f.name}
                                <button type="button" onClick={() => setAttachments((prev) => prev.filter((_, j) => j !== i))}
                                  style={{ background: "none", border: "none", cursor: "pointer", color: "#6366f1", fontSize: 14, lineHeight: 1, padding: 0, marginLeft: 2 }}>×</button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Buttons */}
                      <div style={{ display: "flex", gap: 10 }}>
                        <button onClick={sendEmail} disabled={!bodyText() || sendingEmail}
                          style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 24px", borderRadius: 6, border: "none", background: !bodyText() ? "#d1d5db" : "#2563eb", color: "#fff", fontSize: 13, fontWeight: 700, cursor: !bodyText() ? "not-allowed" : "pointer" }}>
                          {sendingEmail ? <SyncOutlined spin /> : <SendOutlined />}
                          {sendingEmail ? "Sending…" : "Send"}
                        </button>
                        {emailThread.length > 0 && (
                          <button onClick={() => { setShowCompose(false); setAttachments([]); }}
                            style={{ padding: "9px 20px", borderRadius: 6, border: "1px solid #d1d5db", background: "#fff", color: "#374151", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                            Cancel
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Email conversation — Outlook style */}
                  {emailLoading ? (
                    <div style={{ padding: "20px 28px", display: "flex", flexDirection: "column", gap: 12 }}>
                      {[1,2,3].map(i => <Shimmer key={i} h={80} />)}
                    </div>
                  ) : emailThread.length > 0 && (
                    <div className="td-msg-scroll" style={{ overflowY: "auto", minHeight: 300, height: "calc(100vh - 260px)" }}>
                      {[...emailThread].reverse().map((msg, idx) => {
                        const isOut = msg.direction === "outbound";
                        const isFailed = msg.status === "failed";
                        const isMine = isOut && msg.from_email === senderEmail;
                        const senderLabel = isOut ? (isMine ? "Me" : (msg.from_email || "Agent")) : (msg.from_email || "Customer");
                        const msgAtts = parseAttachments(msg.attachments).filter(a => a.url);
                        return (
                          <div key={msg.id || idx} style={{ padding: "18px 28px", borderBottom: idx < emailThread.length - 1 ? "1px solid #f0f2f5" : "none", background: isFailed ? "#fef2f2" : isMine ? "#f8f9ff" : "#fff", borderLeft: isFailed ? "3px solid #dc2626" : "none" }}>
                            {/* Email header row */}
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <div style={{ width: 30, height: 30, borderRadius: "50%", background: isMine ? "linear-gradient(135deg,#6366f1,#4f46e5)" : "linear-gradient(135deg,#16a34a,#15803d)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 800, fontSize: 11, flexShrink: 0 }}>
                                  {(senderLabel[0] || "?").toUpperCase()}
                                </div>
                                <span style={{ fontWeight: 700, fontSize: 13, color: "#1e293b" }}>{senderLabel}</span>
                                {isFailed && (
                                  <span title={msg.error || "Delivery failed"}
                                    style={{ background: "#fee2e2", color: "#b91c1c", border: "1px solid #fecaca", borderRadius: 12, fontSize: 11, fontWeight: 700, padding: "1px 8px", cursor: "default" }}>
                                    ⚠ Failed
                                  </span>
                                )}
                              </div>
                              <span style={{ fontSize: 11, color: "#9ca3af" }}>{timeAgo(msg.sent_at)}</span>
                            </div>
                            {/* Subject */}
                            {msg.subject && <div style={{ fontSize: 12, color: "#64748b", marginBottom: 8, paddingLeft: 38 }}>Sub: {msg.subject}</div>}
                            {/* Body */}
                            <div style={{ fontSize: 13, color: "#374151", lineHeight: 1.75, paddingLeft: 38, wordBreak: "break-word" }}
                              dangerouslySetInnerHTML={{ __html: msg.body || msg.html_body || msg.snippet || "—" }} />
                            {/* Failure reason */}
                            {isFailed && msg.error && (
                              <div style={{ marginTop: 8, paddingLeft: 38, fontSize: 12, color: "#b91c1c" }}>
                                ⚠ Delivery failed: {msg.error}
                              </div>
                            )}
                            {/* Attachments */}
                            {msgAtts.length > 0 && (
                              <div style={{ marginTop: 10, paddingLeft: 38, display: "flex", flexWrap: "wrap", gap: 6 }}>
                                {msgAtts.map((att, ai) => (
                                  <a key={ai} href={att.url} target="_blank" rel="noreferrer" download={att.name}
                                    style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 10px", borderRadius: 4, background: "#f1f5f9", border: "1px solid #e2e8f0", fontSize: 11, color: "#475569", fontWeight: 500, textDecoration: "none", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    📎 {att.name || `Attachment ${ai + 1}`}
                                  </a>
                                ))}
                              </div>
                            )}
                            {/* Reply */}
                            {!isOut && (
                              <div style={{ marginTop: 10, paddingLeft: 38 }}>
                                <button onClick={() => { setSubject(`Re: ${msg.subject || composeSubject}`); setShowCompose(true); }}
                                  style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "4px 12px", borderRadius: 4, border: "1px solid #d1d5db", background: "#fff", color: "#374151", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
                                  ↩ Reply
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default TicketDetail;
